// frontend/src/components/EmployeeAttendanceCard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import {
  AccessTime,
  Login,
  Logout,
  Visibility,
  CheckCircle,
  Cancel,
  Schedule,
  Today,
  LocationOn,
  CheckCircleOutline,
  RadioButtonUnchecked
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '2px solid #bfdbfe',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(59, 130, 246, 0.15)',
    borderColor: '#60a5fa',
  },
  height: '320px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #3b83f698 0%, #8a5cf686 100%)',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  }
}));

const StatusBadge = styled(Box)(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 14px',
  borderRadius: '24px',
  fontSize: '13px',
  fontWeight: 500,
  backgroundColor: 
    status === 'checked_in' ? '#e8f5e9' :
    status === 'checked_out' ? '#e3f2fd' : '#ffebee',
  color:
    status === 'checked_in' ? '#2e7d32' :
    status === 'checked_out' ? '#1565c0' : '#c62828',
}));

const StatBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  padding: theme.spacing(1.2, 1),
  textAlign: 'center',
  flex: 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0f2f5',
  }
}));

const ActionButton = styled(Button)(({ theme, variantcolor }) => ({
  borderRadius: '10px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  flex: 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
  }
}));

function EmployeeAttendanceCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [hasActiveCheckIn, setHasActiveCheckIn] = useState(false);
  const [todayDate, setTodayDate] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  const getEmployeeIdFromToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });
      return response.data.employee_id;
    } catch (error) {
      //console.error('Error getting employee ID:', error);
      return null;
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const fetchTodayAttendance = async () => {
    try {
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!employee_id || !company_id) return;

      const response = await axios.get(
        `${API_URL}/api/manual/today`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
        
          },
          withCredentials: true
        }
      );
      
      setAttendance(response.data.records || []);
      setHasActiveCheckIn(response.data.summary?.hasActiveCheckIn || false);
      
      const today = new Date();
      let config = null;
      try {
        const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cmpRes.data && cmpRes.data.data) {
          config = cmpRes.data.data;
          setCompanyConfig(config);
        }
      } catch (e) {}

      const options = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      if (config?.timezone) {
        options.timeZone = config.timezone;
      }
      setTodayDate(today.toLocaleDateString('en-US', options));
    } catch (error) {
      //console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const company_id = getCompanyIdFromToken();
      const position = await getCurrentLocation();
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/manual/check-in`,
        {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );
      
      setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      await fetchTodayAttendance();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Check-in failed', 
        severity: 'error' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const company_id = getCompanyIdFromToken();
      const position = await getCurrentLocation();
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/manual/check-out`,
        {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );
      
      setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      await fetchTodayAttendance();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Check-out failed', 
        severity: 'error' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '---';
    return timeString.substring(0, 5);
  };

  const getLatestEntry = () => {
    if (!attendance || attendance.length === 0) return null;
    return attendance[attendance.length - 1];
  };

  const latestEntry = getLatestEntry();
  const isCheckedIn = latestEntry && !latestEntry.out_time;
  const isCheckedOut = latestEntry && latestEntry.out_time;

  const getStatus = () => {
    if (isCheckedOut) return { text: 'Checked Out', type: 'checked_out', icon: <CheckCircle fontSize="small" /> };
    if (isCheckedIn) return { text: 'Checked In', type: 'checked_in', icon: <AccessTime fontSize="small" /> };
    return { text: 'Not Checked In', type: 'not_checked', icon: <RadioButtonUnchecked fontSize="small" /> };
  };

  const status = getStatus();

  if (loading) {
    return (
      <StyledCard>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard>
        <CardContent sx={{ p: 2.5 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTime sx={{ color: '#3b82f6', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight="600" color="#1a1a1a">
                Today's Attendance
              </Typography>
            </Box>
            <Chip 
              label={new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: companyConfig?.timezone || undefined })}
              size="small"
              variant="outlined"
              sx={{ borderRadius: '8px', fontSize: '12px', height: '24px' }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Status Badge */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <StatusBadge status={status.type}>
              {status.icon}
              {status.text}
            </StatusBadge>
            {latestEntry?.location && (
              <Tooltip title={`Location: ${latestEntry.location}`}>
                <LocationOn sx={{ fontSize: 16, color: '#999' }} />
              </Tooltip>
            )}
          </Box>

          {/* Stats Row */}
          <Box display="flex" gap={1.5} mb={2.5}>
            <StatBox>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '11px' }}>
                Check In
              </Typography>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: '18px' }}>
                {latestEntry?.in_time ? formatTime(latestEntry.in_time) : '---'}
              </Typography>
            </StatBox>
            <StatBox>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '11px' }}>
                Check Out
              </Typography>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: '18px' }}>
                {latestEntry?.out_time ? formatTime(latestEntry.out_time) : '---'}
              </Typography>
            </StatBox>
            <StatBox>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '11px' }}>
                Total Hours
              </Typography>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: '18px' }}>
                {latestEntry?.formatted_time || '---'}
              </Typography>
            </StatBox>
          </Box>

          {/* Action Buttons - Improved sizing and layout */}
          <Box display="flex" gap={1.5}>
            {!isCheckedIn && !isCheckedOut && (
              <>
                <ActionButton
                  variant="outlined"
                  startIcon={<Login sx={{ fontSize: 18 }} />}
                  onClick={handleCheckIn}
                  disabled={actionLoading}
                  // sx={{
                  //   background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  //   '&:hover': {
                  //     background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  //   }
                  // }}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Check In'}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  startIcon={<Visibility sx={{ fontSize: 18 }} />}
                  onClick={() => navigate('/manual-entry')}
                  sx={{ borderColor: '#e0e0e0', color: '#666' }}
                >
                  History
                </ActionButton>
              </>
            )}
            {isCheckedIn && !isCheckedOut && (
              <>
                <ActionButton
                  variant="outlined"
                  startIcon={<Logout sx={{ fontSize: 18 }} />}
                  onClick={handleCheckOut}
                  disabled={actionLoading}
                  // sx={{
                  //   background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  //   '&:hover': {
                  //     background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  //   }
                  // }}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Check Out'}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  startIcon={<Visibility sx={{ fontSize: 18 }} />}
                  onClick={() => navigate('/manual-entry')}
                  sx={{ borderColor: '#e0e0e0', color: '#666' }}
                >
                  History
                </ActionButton>
              </>
            )}
            {(isCheckedOut || (!isCheckedIn && !isCheckedOut && attendance?.length > 0)) && (
              <ActionButton
                variant="outlined"
                startIcon={<Visibility sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/manual-entry')}
                sx={{ borderColor: '#b0b0b0', color: '#555' }}
              >
                View Full Details
              </ActionButton>
            )}
            {attendance?.length === 0 && !isCheckedIn && (
              <ActionButton
                variant="outlined"
                startIcon={<Visibility sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/manual-entry')}
                sx={{ borderColor: '#e0e0e0', color: '#666' }}
              >
                View History
              </ActionButton>
            )}
          </Box>

          {/* Optional: Show today's date for context */}
          {/* <Box mt={2} pt={1}>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Today sx={{ fontSize: 12 }} />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: companyConfig?.timezone || undefined })}
            </Typography>
          </Box> */}
        </CardContent>
      </StyledCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EmployeeAttendanceCard;